import { useEffect, useRef, useState } from 'react';

interface TabMenuProps {
  tabs: string[];
  activeTab: string;
  setCurrentTab: (tab: string) => void;
  navigate: (path: string) => void;
}

const TabMenu: React.FC<TabMenuProps> = ({
  tabs,
  activeTab,
  setCurrentTab,
  navigate,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [dynamicTab, setDynamicTab] = useState<string | null>(null);
    const [maxVisibleTabs, setMaxVisibleTabs] = useState(4);
    const menuRef = useRef<HTMLDivElement>(null);

    // 🧠 Determine max visible tabs based on screen width
    const calculateMaxVisibleTabs = () => {
        const width = window.innerWidth;
        if (width > 500) return 5;
        if (width > 408) return 4;
        if (width > 385) return 3;
        if (width > 364) return 2;
        return 1;
    };

    // Set maxVisibleTabs on mount and resize
    useEffect(() => {
        const updateVisibleTabs = () => {
            setMaxVisibleTabs(calculateMaxVisibleTabs());
        };

        updateVisibleTabs();
        window.addEventListener('resize', updateVisibleTabs);
        return () => window.removeEventListener('resize', updateVisibleTabs);
    }, []);

    // Click-away logic for hamburger
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [menuOpen]);

    if (!tabs || tabs.length === 0) return null;

    // Build visible tab list
    const activeTabNormalized = activeTab?.toLowerCase();
    const baseVisible = tabs.slice(
        0,
        dynamicTab ? maxVisibleTabs - 1 : maxVisibleTabs
        );

    // Show active tab if space is very tight
    const showOnlyActive = maxVisibleTabs <= 2 && activeTab && tabs.includes(activeTab);

    let visibleTabs: string[];
    if (showOnlyActive) {
        visibleTabs = [activeTab];
    } else {
        visibleTabs = dynamicTab
        ? [...baseVisible.filter(t => t !== dynamicTab), dynamicTab]
        : baseVisible;
    }

    const visibleSet = new Set(visibleTabs);
    const extraTabs = tabs.filter(tab => !visibleSet.has(tab));

    const handleTabClick = (tabName: string, fromHamburger = false) => {
        if (fromHamburger) {
            setDynamicTab(tabName);
        }
        navigate(`/dashboard/${tabName}`);
        setCurrentTab(tabName);
        setMenuOpen(false);
    };

    return (
        <div className="flex flex-wrap items-center justify-center gap-1 py-2 no-print bg-white relative">
        {visibleTabs.map((tabName) => {
            const isActive = activeTabNormalized === tabName.toLowerCase();
            return (
            <button
                type="button"
                key={tabName}
                className={`px-3 py-2 text-[10px] font-semibold rounded-md border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green
                ${
                    isActive
                    ? 'bg-port-green text-white border-port-green'
                    : 'bg-[#21506F] text-white border-white hover:bg-port-green hover:text-white'
                }`}
                onClick={() => handleTabClick(tabName)}
            >
                {tabName}
            </button>
            );
        })}

        {extraTabs.length > 0 && (
            <div className="relative" ref={menuRef}>
            <button
                type="button"
                aria-label="Toggle additional tabs"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
                className="px-3 py-2 text-[10px] font-semibold rounded-md border bg-[#21506F] text-white hover:bg-port-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green"
            >
                ☰
            </button>
            {menuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                {extraTabs.map((tabName) => (
                    <button
                    type="button"
                    key={tabName}
                    onClick={() => handleTabClick(tabName, true)}
                    className="w-full px-4 py-2 text-xs text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green"
                    >
                    {tabName}
                    </button>
                ))}
                </div>
            )}
            </div>
        )}
        </div>
    );
};

export default TabMenu;